/**
 * useUserOrganisations Hook
 *
 * Fetches the authenticated user's organisations and roles
 * for post-login navigation decisions.
 *
 * Usage:
 * ```tsx
 * function AuthRedirectHandler() {
 *   const { isLoading, decision, error } = useUserOrganisations();
 *
 *   if (isLoading) return <Loading />;
 *
 *   if (decision?.type === 'auto-redirect') {
 *     window.location.href = decision.url;
 *     return null;
 *   }
 *
 *   // Handle other cases...
 * }
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth';
import type {
  UserOrganisationsResponse,
  NavigationDecision,
} from '../types/navigation';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.hirenow.com';

interface UseUserOrganisationsResult {
  /** Whether the hook is currently fetching data */
  isLoading: boolean;
  /** Any error that occurred during fetch */
  error: Error | null;
  /** Raw response from the API */
  data: UserOrganisationsResponse | null;
  /** Computed navigation decision based on user's orgs/roles */
  decision: NavigationDecision | null;
  /** Re-fetches the organisations data */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch user organisations and compute navigation decision.
 *
 * @returns Object with loading state, error, data, decision, and refetch function
 */
export function useUserOrganisations(): UseUserOrganisationsResult {
  const { user, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<UserOrganisationsResponse | null>(null);
  const [decision, setDecision] = useState<NavigationDecision | null>(null);

  const fetchOrganisations = useCallback(async () => {
    if (!isAuthenticated || !user?.idToken) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/v1/auth/me/organisations`, {
        headers: {
          Authorization: `Bearer ${user.idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch organisations: ${response.status}`);
      }

      const result: UserOrganisationsResponse = await response.json();
      setData(result);
      setDecision(determineNavigation(result));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setDecision({ type: 'no-access', reason: 'Failed to load organisations' });
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.idToken]);

  useEffect(() => {
    fetchOrganisations();
  }, [fetchOrganisations]);

  return { isLoading, error, data, decision, refetch: fetchOrganisations };
}

/**
 * Determine the navigation action based on user's organisations.
 *
 * Decision logic:
 * 1. Admin only (no organisations) → redirect to admin app
 * 2. Single org + single role → auto-redirect to that app
 * 3. Multiple orgs or roles → show selection page
 * 4. No organisations and not admin → no access error
 *
 * @param data - The user organisations response from API
 * @returns NavigationDecision indicating what action to take
 */
export function determineNavigation(
  data: UserOrganisationsResponse
): NavigationDecision {
  const { is_admin, organisations } = data;

  // Case 1: Admin only (no organisations)
  if (is_admin && organisations.length === 0) {
    return {
      type: 'admin-only',
      url: import.meta.env.VITE_ADMIN_APP_URL || 'https://admin.hirenow.com',
    };
  }

  // Case 2: Admin with org memberships → show selection so they can choose
  // between admin console and org-specific portals
  if (is_admin && organisations.length > 0) {
    return {
      type: 'show-selection',
      organisations,
      isAdmin: is_admin,
    };
  }

  // Case 3: Single org with single role → auto-redirect
  if (organisations.length === 1 && organisations[0]!.roles.length === 1) {
    const org = organisations[0]!;
    const role = org.roles[0]!;
    const baseUrl = getRoleAppUrl(role);

    return {
      type: 'auto-redirect',
      url: `${baseUrl}/${org.slug}`,
      orgSlug: org.slug,
      role: role,
    };
  }

  // Case 4: Multiple orgs or roles → show selection
  if (organisations.length > 0) {
    return {
      type: 'show-selection',
      organisations,
      isAdmin: is_admin,
    };
  }

  // Case 5: No organisations and not admin
  return {
    type: 'no-access',
    reason:
      'You do not belong to any organisation. Please contact your administrator.',
  };
}

/**
 * Get the app URL for a given role.
 *
 * @param role - The role name (admin, partner, supplier, hirer)
 * @returns The base URL for that role's application
 */
function getRoleAppUrl(role: string): string {
  const urls: Record<string, string> = {
    admin: import.meta.env.VITE_ADMIN_APP_URL || 'https://admin.hirenow.com',
    partner: import.meta.env.VITE_PARTNER_APP_URL || 'https://partner.hirenow.com',
    supplier:
      import.meta.env.VITE_SUPPLIER_APP_URL || 'https://supplier.hirenow.com',
    hirer: import.meta.env.VITE_HIRER_APP_URL || 'https://hirer.hirenow.com',
  };

  return urls[role.toLowerCase()] ?? urls['hirer']!;
}
